/**
 * 비로그인 업로드 "게재 준비" 상태 스트림의 순수 리듀서 — DOM 의존 없음(jest node 환경에서 검증).
 * 네트워크 워처는 uploadStatusStream.ts. 서버 이벤트:
 *   {type:'state', status, percent, stage} 1회 → {type:'progress', percent, etaSec, stage}* →
 *   {type:'done'} | {type:'failed', message} | {type:'timeout'} | {type:'replaced'} ; {type:'ping'} 20초.
 * 끊김(앱 전환·네트워크)은 재접속으로 복구한다 — 서버가 접속 시점 상태를 다시 보내 주므로 안전하다.
 * 재접속이 거듭 실패하면 "서버에서 계속돼요" 로 마무리한다(무한 대기 금지).
 */
import { BADGE_TEXT, transcodeBadge, BadgeStage } from './transcodeBadge';

export type UploadWatchKind = 'waiting' | 'working' | 'done' | 'failed' | 'detached';

export interface UploadWatchState {
  kind: UploadWatchKind;
  text: string;
  percent: number | null;
  stage: BadgeStage | null;
  hadPrepare: boolean;
  message?: string | null;
}

export const UPLOAD_WATCH_TEXT = {
  initial: '올렸어요 · 게재 준비 중',
  done: '게재 준비가 끝났어요. 관리자가 확인하면 화면에 나와요.',
  failed: '게재 준비에 실패했어요. 다른 파일로 다시 올려 주세요.',
  detached: '준비는 서버에서 계속돼요. 승인되면 화면에 나와요.',
} as const;

export const initialUploadWatchState = (): UploadWatchState =>
  ({ kind: 'waiting', text: UPLOAD_WATCH_TEXT.initial, percent: null, stage: null, hadPrepare: false });

export interface UploadStatusEvent {
  type: string;
  status?: string;
  percent?: number;
  stage?: BadgeStage;
  message?: string | null;
}

const toStage = (s: unknown): BadgeStage | null =>
  s === 'prepare' || s === 'queued' || s === 'encode' ? s : null;

export function applyUploadStatusEvent(state: UploadWatchState, ev: UploadStatusEvent): UploadWatchState {
  if (state.kind === 'done' || state.kind === 'failed') return state; // 종료 상태는 덮지 않는다
  switch (ev.type) {
    case 'done':
      return { ...state, kind: 'done', text: UPLOAD_WATCH_TEXT.done, percent: 100, stage: null };
    case 'failed':
      return { ...state, kind: 'failed', text: UPLOAD_WATCH_TEXT.failed, percent: null, stage: null, message: ev.message ?? null };
    case 'timeout':
    case 'replaced':
      return { ...state, kind: 'detached', text: UPLOAD_WATCH_TEXT.detached, percent: null };
    case 'state':
    case 'progress': {
      const stage = toStage(ev.stage) ?? state.stage;
      const status = ev.type === 'state' ? (ev.status ?? 'processing') : (stage === 'queued' ? 'pending' : 'processing');
      const hadPrepare = state.hadPrepare || stage === 'prepare';
      const prev = state.percent ?? 0;
      // 같은 단계에서는 % 가 내려가지 않는다(순서 뒤집힘·재전송 방어). 단계가 바뀌면 리셋 허용.
      const raw = typeof ev.percent === 'number' ? ev.percent : prev;
      const percent = stage === state.stage ? Math.max(prev, raw) : raw;
      const badge = transcodeBadge({
        entry: { status: status as any, progress: percent, stage: stage ?? undefined, hadPrepare },
      });
      return {
        ...state,
        kind: badge.kind === 'queued' ? 'waiting' : 'working',
        text: badge.kind === 'none' ? BADGE_TEXT.preparing : badge.text,
        percent: badge.percent,
        stage,
        hadPrepare,
      };
    }
    default:
      return state; // ping 등
  }
}
